import { useState, type ReactNode } from 'react'
import {
    useFloating,
    useHover,
    useInteractions,
    useDismiss,
    useRole,
    offset,
    flip,
    shift,
    autoUpdate,
    FloatingPortal,
    type Placement,
} from '@floating-ui/react'
import css from './Tooltip.module.css'
import {TextSmall} from "@/components/ui/Text";
import useIsMobile from '@/hooks/useIsMobile'

interface TooltipProps {
    title: string
    placement?: Placement
    delay?: number
    children: ReactNode
}

const Tooltip = ({ title, placement = 'right', delay = 150, children }: TooltipProps) => {
    // Hover tooltips don't belong on touch: a tap fires pointerenter with no matching
    // mouseleave, so the label opens and sticks on screen. Disable the interaction on mobile
    // entirely (not just hide the stuck label) — the icon still works, just without a tooltip.
    const isMobile = useIsMobile()
    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen && !isMobile,
        onOpenChange: setIsOpen,
        placement,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    const hover = useHover(context, { enabled: !isMobile, delay: { open: delay, close: 0 }, restMs: 40, move: false })
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role])

    return (
        <>
            <span ref={refs.setReference} {...getReferenceProps()} className={css.reference}>
                {children}
            </span>
            {isOpen && !isMobile && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className={css.tooltip}
                    >
                        <TextSmall>
                            {title}
                        </TextSmall>
                    </div>
                </FloatingPortal>
            )}
        </>
    )
}

export default Tooltip
